import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { UerTotal_AlcTypeEnum as UerTotalAlcTypeEnum } from 'gql/graphql';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { uerActKC3TotalsGql } from '../kc3TotalsQueries';

export const useTotalsData = (kc3Id: number) => {
    const [collapseOthers, setCollapseOthers] = useState<boolean>(false);

    const variables = useMemo(
        () => ({
            actKC3Id: kc3Id,
        }),
        [kc3Id],
    );

    const [data] = useRequest({
        requestId: 'kc3Totals',
        query: uerActKC3TotalsGql,
        variables,
        withEffect: !!kc3Id,
    });

    const [resetData] = useResetStoreData({
        requestId: 'kc3Totals',
    });

    const groupedData = useMemo(() => {
        if (!data?.uerActKC3Totals?.data) {
            return [];
        }
        if (!collapseOthers) {
            return data?.uerActKC3Totals?.data;
        }

        return data?.uerActKC3Totals?.data?.filter(
            (item) => !(item?.totalType === UerTotalAlcTypeEnum.Other && item?.isOtherCost),
        );
    }, [collapseOthers, data?.uerActKC3Totals?.data]);

    const toggleOthersCollapse = useCallback(() => {
        setCollapseOthers((p) => !p);
    }, []);

    const hasNotOthersData = useMemo(
        () => data?.uerActKC3Totals?.data?.every((row) => !row?.isOtherCost) ?? false,
        [data?.uerActKC3Totals?.data],
    );

    useEffect(
        () => () => {
            resetData(undefined);
        },
        [resetData],
    );

    return useMemo(
        () => ({
            groupedData,
            toggleOthersCollapse,
            othersCollapseState: collapseOthers,
            hasNotOthersData,
        }),
        [collapseOthers, groupedData, hasNotOthersData, toggleOthersCollapse],
    );
};
